import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from '@usas/ui-web/wrappers/ReactRouterDom';

import NavLinks from '../../../meet/NavLinks';
import HostNavLinks from '../../HostNavLinks';
import HostRegistrationConstants from '../HostRegistrationConstants';

import validate from './OmeMeetsDetailValidation';

import useOmeMeetData from '../../../../state/omeMeet/UseOmeMeetData';
import useOmeMeetEntryData from '../../../../state/omeMeetEntry/UseOmeMeetEntryData';
import useOmeMeetEntryInvitationsData from '../../../../state/omeMeetEntryInvitations/UseOmeMeetEntryInvitationsData';
import useOmeMeetDateValidationData from '../../../../state/omeMeetDateValidation/UseOmeMeetDateValidationData';

import useDateTimeData from '@usas/ui-web/state/datetime/UseDateTimeData';
import useOrgUnitData from '@usas/ui-web/state/orgUnit/UseOrgUnitData';
import useEnvironmentVariableData from '@usas/ui-web/state/environmentVariable/UseEnvironmentVariableData';
import useLeftNavModalData from '@usas/ui-web/state/leftNavModal/UseLeftNavModalData';
import useNavRoutes from '@usas/ui-web/state/security/UseNavRoutes';

import useForm from '@usas/ui-web/utils/UseForm';
import HierarchicalDataUtils from '@usas/ui-web/utils/HierarchicalDataUtils';
import Constants from '@usas/ui-web/utils/Constants';
import { formatDate, formatDateTime, formatDateTimeTwelveHourClock, getTodaysDate } from '@usas/ui-web/utils/DateFunctions';
import ToIntIfInt from '@usas/ui-web/utils/ToIntIfInt';

const INITIAL_SELECTED_OME_ENTRY_STATE = {
  initialLoadComplete: false,
  selectedOmeMeetEntryId: Constants.DEFAULT_ID
};

const INITIAL_VIEW_STATE = {
  prePutOmeMeetEntryStateObjData: {},
  updatedOmeEntryInvitationArray: [],
  tryPostOmeMeetEntryInvitations: false,
  tryGetOmeMeetEntry: false,
  submitButton: '',
  doNotTryRedirect: false,
  tryRedirect: false,
  organizationInvitations: [],
  omeMeetPriceId: Constants.DEFAULT_ID,
  gridData: []
};

const INITIAL_FORM_STATE = {
  meetStartDate: Constants.BLANK_DATE_STRING,
  publishDate: Constants.BLANK_DATETIME_STRING,
  omeMeetName: '',
  isMainEntry: false,
  meetEntryStartDate: Constants.BLANK_DATETIME_STRING,
  meetEntryEndDate: Constants.BLANK_DATETIME_STRING,
  meetEntryChangeDeadline: Constants.BLANK_DATETIME_STRING,
  meetEntryStartDateTwelveHourClock: Constants.BLANK_DATETIME_STRING,
  meetEntryEndDateTwelveHourClock: Constants.BLANK_DATETIME_STRING,
  meetEntryChangeDeadlineTwelveHourClock: Constants.BLANK_DATETIME_STRING,
  relayEvents: '',
  allowRelayOnlySwimmer: '',
  maxNumberOfAthletesForMeet: '',
  maxNumberOfAthletesCountsTowardsMainOmeMeet: '',
  includeRelayOnlySwimmersInMaxNumber: '',
  nationalTeamOnly: '',
  orgUnit: [],
  hasTimeStandards: '',
  hasQualifyingWindow: '',
  timeStandardsQualifyingStartDate: Constants.BLANK_DATE_STRING,
  timeStandardsQualifyingEndDate: Constants.BLANK_DATE_STRING,
  costPerTeam: '',
  costPerCredential: '',
  costPerIndividualAthlete: '',
  costPerIndividualInternationalAthlete: '',
  costPerInternationalStaff: '',
  costPerRelayOnlySwimmer: '',
  costPerAthleteIndividualEventEntry: '',
  costPerAthleteIndividualBonusEventEntry: '',
  costPerRelayEventEntry: '',
  currentCostPerTeam: '',
  currentCostPerCredential: '',
  currentCostPerIndividualAthlete: '',
  currentCostPerIndividualInternationalAthlete: '',
  currentCostPerInternationalStaff: '',
  currentCostPerRelayOnlySwimmer: '',
  currentCostPerAthleteIndividualEventEntry: '',
  currentCostPerAthleteIndividualBonusEventEntry: '',
  currentCostPerRelayEventEntry: '',
  showCostPerIndividualInternationalAthlete: false,
  showCostPerInternationalStaff: false,
  showCostPerAthleteIndividualBonusEventEntry: false,
  showCostPerRelayOnlySwimmer: false,
  showCostPerRelayEventEntry: false
};

const INITIAL_MODAL_STATE = {
  displayPopUp: false,
  modalTitle: 'Delete Additional Purchase Item?',
  omeFeeTypeName: '',
  amount: '',
  description: '',
  omeMeetPriceId: Constants.DEFAULT_ID
};

const useOmeMeetsDetail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedOmeMeetEntryState, setSelectedOmeMeetEntryState] = useState(INITIAL_SELECTED_OME_ENTRY_STATE);
  const [state, setState] = useState(INITIAL_VIEW_STATE);
  const [modalState, setModalState] = useState(INITIAL_MODAL_STATE);
  const { isReadyToRoute } = useNavRoutes();
  const { omeMeetState } = useOmeMeetData();
  const { omeMeetEntryState, getOmeMeetEntry, putOmeMeetEntry } = useOmeMeetEntryData();
  const { omeMeetEntryInvitationsState, postOmeMeetEntryInvitations } = useOmeMeetEntryInvitationsData();
  const { omeMeetDateValidationState, getOmeMeetDateValidation } = useOmeMeetDateValidationData();
  const { dateTimeState } = useDateTimeData();
  const { orgUnitState, getOrgUnits } = useOrgUnitData();
  const { INDIVIDUAL_INTL_ATHLETE_OME_FEE_TYPE_ID, INTL_STAFF_OME_FEE_TYPE_ID, FINA_ORG_UNIT_ID, TEAM_OME_FEE_TYPE_ID,
    CREDENTIAL_OME_FEE_TYPE_ID, INDIVIDUAL_ATHLETE_OME_FEE_TYPE_ID, RELAY_ONLY_SWIMMER_OME_FEE_TYPE_ID, ATHLETE_INDIVIDUAL_EVENT_ENTRY_OME_FEE_TYPE_ID,
    ATHLETE_INDIVIDUAL_BONUS_EVENT_ENTRY_OME_FEE_TYPE_ID, RELAY_EVENT_ENTRY_OME_FEE_TYPE_ID } = useEnvironmentVariableData();
  const { leftNavModalState, setLeftNavModalState } = useLeftNavModalData();
  const { formState, errorState, onFormValueChanged, isSubmitting, handleSubmit, setIsDirty,
    isDirty, setFormData, setFormState, setErrors } = useForm(INITIAL_FORM_STATE, submitFormCallback, validate);

  const tryValidateBeforeRedirect = async (submitButton, omeMeetPriceId) => {
    const errors = await validate(formState) || {};
    setErrors(errors);

    let updatedOmeMeetEntry = JSON.parse(JSON.stringify(omeMeetEntryState.objData));
    updatedOmeMeetEntry = editOmeMeetEntryCopy(updatedOmeMeetEntry, formState, selectedOmeMeetEntryState.selectedOmeMeetEntryId);

    let error = '';
    const issueWithMaxNumberOfAthletesForMeetReturn = issueWithMaxNumberOfAthletesForMeet(updatedOmeMeetEntry);
    if (issueWithMaxNumberOfAthletesForMeetReturn?.issue === true) {
      error = issueWithMaxNumberOfAthletesForMeetReturn?.errorMessage;
    }
    const issueWithOmeMeetNameReturn = issueWithOmeMeetName(updatedOmeMeetEntry);
    if (issueWithOmeMeetNameReturn?.issue === true) {
      error = error ? error + `\n\n ${issueWithOmeMeetNameReturn?.errorMessage}` : issueWithOmeMeetNameReturn?.errorMessage;
    }
    if (error !== '') {
      setErrors({ ...errorState, error: error });
    }
    if (Object.keys(errors).length === 0 && error === '') {
      setState({ ...state, tryRedirect: true, submitButton: submitButton, omeMeetPriceId: omeMeetPriceId ? omeMeetPriceId : Constants.DEFAULT_ID });
    }
  };

  const tryValidateBeforeShowingDeleteAdditionalPurchaseItemPopUp = async (purchaseItem) => {
    const errors = await validate(formState) || {};
    setErrors(errors);

    let updatedOmeMeetEntry = JSON.parse(JSON.stringify(omeMeetEntryState.objData));
    updatedOmeMeetEntry = editOmeMeetEntryCopy(updatedOmeMeetEntry, formState, selectedOmeMeetEntryState.selectedOmeMeetEntryId);

    let error = '';
    const issueWithMaxNumberOfAthletesForMeetReturn = issueWithMaxNumberOfAthletesForMeet(updatedOmeMeetEntry);
    if (issueWithMaxNumberOfAthletesForMeetReturn?.issue === true) {
      error = issueWithMaxNumberOfAthletesForMeetReturn?.errorMessage;
    }
    const issueWithOmeMeetNameReturn = issueWithOmeMeetName(updatedOmeMeetEntry);
    if (issueWithOmeMeetNameReturn?.issue === true) {
      error = error ? error + `\n\n ${issueWithOmeMeetNameReturn?.errorMessage}` : issueWithOmeMeetNameReturn?.errorMessage;
    }
    if (error !== '') {
      setErrors({ ...errorState, error: error });
    }
    //Show popup if there are no form validation errors
    if (Object.keys(errors).length === 0 && error === '') {
      setModalState({
        ...modalState,
        displayPopUp: true,
        omeFeeTypeName: purchaseItem.omeFeeType?.typeName || '',
        amount: '$' + purchaseItem.amount || '',
        description: purchaseItem.description || '',
        omeMeetPriceId: purchaseItem.omeMeetPriceId || Constants.DEFAULT_ID
      });
    }
  };

  const onNextButtonClicked = (e) => {
    if (e?.preventDefault) {
      e.preventDefault();
    }
    if (isDirty === false) {
      tryValidateBeforeRedirect(HostRegistrationConstants.CONTINUE);
    }
    else {
      handleSubmit();
      setState({ ...state, submitButton: HostRegistrationConstants.CONTINUE });
    }
  };

  const onBackButtonClicked = (e) => {
    if (e?.preventDefault) {
      e.preventDefault();
    }
    if (omeMeetState.route !== '') {
      navigate(NavLinks.MEET_HOST_OME_MEETS);
    }
    else {
      navigate(HostNavLinks.OME_MEET_OME_MEETS);
    }
  };

  const onAddAdditionalPurchaseItem = (e) => {
    if (e?.preventDefault) {
      e.preventDefault();
    }
    tryValidateBeforeRedirect(HostRegistrationConstants.ADD);
  }

  const onEditAdditionalPurchaseItem = (e, omeMeetPriceId) => {
    if (e?.preventDefault) {
      e.preventDefault();
    }
    tryValidateBeforeRedirect(HostRegistrationConstants.EDIT, omeMeetPriceId);
  }

  const onDeleteAdditionalPurchaseItemClicked = (e, purchaseItem) => {
    if (e?.preventDefault) {
      e.preventDefault();
    }
    tryValidateBeforeShowingDeleteAdditionalPurchaseItemPopUp(purchaseItem);
  };

  const onDeleteAdditionalPurchaseItem = (e) => {
    if (e?.preventDefault) {
      e.preventDefault();
    }
    let updatedOmeMeetEntry = JSON.parse(JSON.stringify(omeMeetEntryState.objData));
    if (isDirty === true) {
      updatedOmeMeetEntry = editOmeMeetEntryCopy(updatedOmeMeetEntry, formState, selectedOmeMeetEntryState.selectedOmeMeetEntryId);
    }
    const selectedOmeMeetEntryObjIndex = updatedOmeMeetEntry.omeMeetEntry?.findIndex(x => x.omeMeetEntryId === selectedOmeMeetEntryState.selectedOmeMeetEntryId);
    const selectedOmeMeetPriceIndex = updatedOmeMeetEntry.omeMeetEntry[selectedOmeMeetEntryObjIndex]?.omeMeetPrice?.findIndex(x =>
      x.omeMeetPriceId === modalState.omeMeetPriceId);

    const updatedOmeEntryInvitationArray =
      formState.orgUnit?.map(x => {
        return (
          {
            orgUnitId: x.id
          }
        )
      });

    //Check to see if the invitations are different and need to be saved
    let needToUpdateInvitiations = false;
    const selectedOmeMeetEntryObj = omeMeetEntryState.objData?.omeMeetEntry?.find(x => x.omeMeetEntryId === selectedOmeMeetEntryState.selectedOmeMeetEntryId);

    if (selectedOmeMeetEntryObj.omeEntryInvitation?.length !== updatedOmeEntryInvitationArray.length) {
      needToUpdateInvitiations = true;
    }
    else {
      for (let i = 0; i < updatedOmeEntryInvitationArray.length; i++) {
        let existingOrgUnitId = selectedOmeMeetEntryObj.omeEntryInvitation?.find(x => x.orgUnitId === updatedOmeEntryInvitationArray[i].orgUnitId);
        if (!existingOrgUnitId) {
          needToUpdateInvitiations = true;
        }
      }
    }

    onModalCanceled();
    if (selectedOmeMeetPriceIndex > -1) {
      updatedOmeMeetEntry.omeMeetEntry[selectedOmeMeetEntryObjIndex]?.omeMeetPrice?.splice(selectedOmeMeetPriceIndex, 1);
      putOmeMeetEntry(omeMeetState?.objData?.omeMeetId, updatedOmeMeetEntry);
      if (needToUpdateInvitiations === true) {
        setState({ ...state, updatedOmeEntryInvitationArray: updatedOmeEntryInvitationArray, tryPostOmeMeetEntryInvitations: true, doNotTryRedirect: true });
      }
      else {
        setState({ ...state, tryGetOmeMeetEntry: true, doNotTryRedirect: true });
      }
    }
  };

  const onModalCanceled = (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }

    setModalState(INITIAL_MODAL_STATE);
  };

  const getNameForIdRecurse = (id, node) => {
    let name = null;
    if (id === node.id) {
      name = { name: node.name, node: node };
    } else if (Array.isArray(node.children) && node.children.length > 0) {
      node.children.forEach(child => {
        if (!name) {
          name = getNameForIdRecurse(id, child);
        }
      });
    }

    return name;
  };

  const GetNameIdPairsRemoveAnyChildren = (treeData, idValues, lastSelectedOrgUnitId) => {
    //Remove any children selections if the lastSelectedOrgUnitId is a parent of those children selections
    const newValues = JSON.parse(JSON.stringify(idValues));
    let newObj = null;
    treeData.forEach(node => {
      if (!newObj) {
        const lastSelectedOrgUnitIdName = getNameForIdRecurse(lastSelectedOrgUnitId, node);
        if (lastSelectedOrgUnitIdName) {
          for (let i = 0; i < idValues.length; i++) {
            if (idValues[i] !== lastSelectedOrgUnitId) {
              const childSelected = getNameForIdRecurse(idValues[i], lastSelectedOrgUnitIdName.node);
              if (childSelected) {
                const childIndex = newValues.findIndex(x => x === idValues[i]);
                newValues.splice(childIndex, 1);
              }
            }
          }
        }
      }
    });

    return newValues;
  };

  const GetNameIdPairsRemoveAnyParents = (treeData, idValues) => {
    //Remove any parent selections if any children are selected
    const newValues = [];
    if (Array.isArray(idValues) && idValues.length > 0) {
      idValues.forEach(id => {
        let newObj = null;
        treeData.forEach(node => {
          if (!newObj) {
            const name = getNameForIdRecurse(id, node);

            if (name) {
              let addToNewValues = true;
              for (let i = 0; i < idValues.length; i++) {
                if (idValues[i] !== id) {
                  const childSelected = getNameForIdRecurse(idValues[i], name.node);
                  if (childSelected) {
                    addToNewValues = false;
                  }
                }
              }
              if (addToNewValues === true) {
                newObj = { id: id, name: name.name };
              }
            }
          }
        });

        if (newObj) {
          newValues.push(newObj);
        }
      });
    }

    return newValues;
  };

  const onOrgUnitFormValueChanged = (orgUnit, newValue) => {
    let allSelectedOrgUnitIds = [];
    let lastSelectedOrgUnitId = '';

    for (let i = 0; i < newValue.length; i++) {
      allSelectedOrgUnitIds.push(newValue[i].id);
      const currentlyInFormState = formState.orgUnit.find(x => x.id === newValue[i].id);
      if (!currentlyInFormState) {
        lastSelectedOrgUnitId = newValue[i].id;
      }
    }

    const updatedOrgUnitIdsWithoutChildren = GetNameIdPairsRemoveAnyChildren(orgUnitState.treeData, allSelectedOrgUnitIds, lastSelectedOrgUnitId);
    const updatedOrgUnitIdsWithoutChildrenAndParents = GetNameIdPairsRemoveAnyParents(orgUnitState.treeData, updatedOrgUnitIdsWithoutChildren);
    onFormValueChanged(orgUnit, updatedOrgUnitIdsWithoutChildrenAndParents);
  };

  const selectionsContainFinaOrgUnit = (formState) => {
    let selectionsContainFinaOrgUnit = false;
    const finaOrgUnits = orgUnitState.arrayData.find(x => x.id === FINA_ORG_UNIT_ID);

    //Check selections for FINA
    for (let i = 0; i < formState.orgUnit.length; i++) {
      //Check to see if FINA is selected
      if (formState.orgUnit[i]?.id === finaOrgUnits?.id) {
        selectionsContainFinaOrgUnit = true;
        break;
      }
      //Check to see if a FINA continent is selected
      for (let j = 0; j < finaOrgUnits.children?.length; j++) {
        if (formState.orgUnit[i]?.id === finaOrgUnits.children[j].id) {
          selectionsContainFinaOrgUnit = true;
          break;
        }
        //Check to see if a FINA country is selected
        for (let k = 0; k < finaOrgUnits.children[j]?.children?.length; k++) {
          if (formState.orgUnit[i]?.id === finaOrgUnits.children[j]?.children[k]?.id) {
            selectionsContainFinaOrgUnit = true;
            break;
          }
        }
      }
    }

    return selectionsContainFinaOrgUnit;
  };

  const editOmeMeetEntryCopy = (updatedOmeMeetEntry, formState, selectedOmeMeetEntryId) => {
    if (selectedOmeMeetEntryId >= 0) {
      const selectedOmeMeetEntryObjIndex = updatedOmeMeetEntry.omeMeetEntry?.findIndex(x => x.omeMeetEntryId === selectedOmeMeetEntryId);
      if (selectedOmeMeetEntryObjIndex > -1) {
        //Delete cost per individual international athlete and cost per international staff if all FINA organizations are unselected
        if (selectionsContainFinaOrgUnit(formState) === false) {
          const selectedAthleteOmeFeeTypeIdIndex = updatedOmeMeetEntry.omeMeetEntry[selectedOmeMeetEntryObjIndex].omeMeetPrice?.findIndex(x => x.omeFeeTypeId === INDIVIDUAL_INTL_ATHLETE_OME_FEE_TYPE_ID);
          if (selectedAthleteOmeFeeTypeIdIndex > -1) {
            updatedOmeMeetEntry.omeMeetEntry[selectedOmeMeetEntryObjIndex].omeMeetPrice.splice(selectedAthleteOmeFeeTypeIdIndex, 1);
          }
          const selectedStaffOmeFeeTypeIdIndex = updatedOmeMeetEntry.omeMeetEntry[selectedOmeMeetEntryObjIndex].omeMeetPrice?.findIndex(x => x.omeFeeTypeId === INTL_STAFF_OME_FEE_TYPE_ID);
          if (selectedStaffOmeFeeTypeIdIndex > -1) {
            updatedOmeMeetEntry.omeMeetEntry[selectedOmeMeetEntryObjIndex].omeMeetPrice.splice(selectedStaffOmeFeeTypeIdIndex, 1);
          }
        }

        //If omeMeetQualificationDate object has not been created
        if (updatedOmeMeetEntry.omeMeetEntry[selectedOmeMeetEntryObjIndex].omeMeetQualificationDate?.length === 0) {
          updatedOmeMeetEntry.omeMeetEntry[selectedOmeMeetEntryObjIndex].omeMeetQualificationDate = [{
            omeMeetEntryId: updatedOmeMeetEntry.omeMeetEntry[selectedOmeMeetEntryObjIndex].omeMeetEntryId,
            timeStandardsQualifyingStartDate: formState.hasTimeStandards === true ? (formState.hasQualifyingWindow === true ?
              ((formState.timeStandardsQualifyingStartDate !== Constants.BLANK_DATE_STRING ? formState.timeStandardsQualifyingStartDate : null)) : Constants.DEFAULT_MIN_DATE)
              : null,
            timeStandardsQualifyingEndDate: formState.hasTimeStandards === true ? (formState.hasQualifyingWindow === true ?
              ((formState.timeStandardsQualifyingEndDate !== Constants.BLANK_DATE_STRING ? formState.timeStandardsQualifyingEndDate : null)) :
              formState.meetEntryEndDate.substring(0, 10))
              : null
          }]
        }
        // If omeMeetQualificationDate object exists
        else if (updatedOmeMeetEntry.omeMeetEntry[selectedOmeMeetEntryObjIndex].omeMeetQualificationDate?.length > 0) {
          updatedOmeMeetEntry.omeMeetEntry[selectedOmeMeetEntryObjIndex].omeMeetQualificationDate = [{
            ...updatedOmeMeetEntry.omeMeetEntry[selectedOmeMeetEntryObjIndex].omeMeetQualificationDate[0],
            timeStandardsQualifyingStartDate: formState.hasTimeStandards === true ? (formState.hasQualifyingWindow === true ?
              ((formState.timeStandardsQualifyingStartDate !== Constants.BLANK_DATE_STRING ? formState.timeStandardsQualifyingStartDate : null)) : Constants.DEFAULT_MIN_DATE)
              : null,
            timeStandardsQualifyingEndDate: formState.hasTimeStandards === true ? (formState.hasQualifyingWindow === true ?
              ((formState.timeStandardsQualifyingEndDate !== Constants.BLANK_DATE_STRING ? formState.timeStandardsQualifyingEndDate : null)) :
              formState.meetEntryEndDate.substring(0, 10))
              : null
          }]
        }

        if (updatedOmeMeetEntry.omeMeetEntry[selectedOmeMeetEntryObjIndex]?.omeMeetPrice.length > 0) {
          updatedOmeMeetEntry.omeMeetEntry[selectedOmeMeetEntryObjIndex].omeMeetPrice = updatedOmeMeetEntry.omeMeetEntry[selectedOmeMeetEntryObjIndex].omeMeetPrice.filter(x => x.omeFeeType?.isAdditionalPurchase === true);
        }

        updatedOmeMeetEntry.omeMeetEntry[selectedOmeMeetEntryObjIndex] = {
          ...updatedOmeMeetEntry.omeMeetEntry[selectedOmeMeetEntryObjIndex],
          omeMeetName: formState.omeMeetName.trim() || '',
          status: HostRegistrationConstants.COMPLETE_STATUS,
          meetEntryStartDate: formState.meetEntryStartDate,
          meetEntryEndDate: formState.meetEntryEndDate,
          meetEntryChangeDeadline: formState.meetEntryChangeDeadline,
          maxNumberOfAthletesForMeet: formState.maxNumberOfAthletesForMeet.toString().replace(',', '') || null,
          maxNumberOfAthletesCountsTowardsMainOmeMeet: formState.maxNumberOfAthletesCountsTowardsMainOmeMeet ?? null,
          includeRelayOnlySwimmersInMaxNumber: formState.relayEvents === true && formState.allowRelayOnlySwimmer === true ? (formState.includeRelayOnlySwimmersInMaxNumber ?? null) : null,
          nationalTeamOnly: formState.nationalTeamOnly ?? null,
          omeMeetPrice: [
            ...updatedOmeMeetEntry.omeMeetEntry[selectedOmeMeetEntryObjIndex].omeMeetPrice,
            ...(formState.costPerTeam !== '' ? [{ omeMeetPriceId: updatedOmeMeetEntry.omeMeetEntry[selectedOmeMeetEntryObjIndex].omeMeetPrice?.find(x => x.omeFeeTypeId === TEAM_OME_FEE_TYPE_ID)?.omeMeetPriceId || undefined, omeMeetEntryId: selectedOmeMeetEntryState.selectedOmeMeetEntryId >= 0 ? selectedOmeMeetEntryState.selectedOmeMeetEntryId : undefined, omeFeeTypeId: TEAM_OME_FEE_TYPE_ID, amount: formState.costPerTeam }] : []),
            ...(formState.costPerCredential !== '' ? [{ omeMeetPriceId: updatedOmeMeetEntry.omeMeetEntry[selectedOmeMeetEntryObjIndex].omeMeetPrice?.find(x => x.omeFeeTypeId === CREDENTIAL_OME_FEE_TYPE_ID)?.omeMeetPriceId || undefined, omeMeetEntryId: selectedOmeMeetEntryState.selectedOmeMeetEntryId >= 0 ? selectedOmeMeetEntryState.selectedOmeMeetEntryId : undefined, omeFeeTypeId: CREDENTIAL_OME_FEE_TYPE_ID, amount: formState.costPerCredential }] : []),
            ...(formState.costPerIndividualAthlete !== '' ? [{ omeMeetPriceId: updatedOmeMeetEntry.omeMeetEntry[selectedOmeMeetEntryObjIndex].omeMeetPrice?.find(x => x.omeFeeTypeId === INDIVIDUAL_ATHLETE_OME_FEE_TYPE_ID)?.omeMeetPriceId || undefined, omeMeetEntryId: selectedOmeMeetEntryState.selectedOmeMeetEntryId >= 0 ? selectedOmeMeetEntryState.selectedOmeMeetEntryId : undefined, omeFeeTypeId: INDIVIDUAL_ATHLETE_OME_FEE_TYPE_ID, amount: formState.costPerIndividualAthlete }] : []),
            ...(formState.costPerIndividualInternationalAthlete !== '' && formState.showCostPerIndividualInternationalAthlete === true ? [{ omeMeetPriceId: updatedOmeMeetEntry.omeMeetEntry[selectedOmeMeetEntryObjIndex].omeMeetPrice?.find(x => x.omeFeeTypeId === INDIVIDUAL_INTL_ATHLETE_OME_FEE_TYPE_ID)?.omeMeetPriceId || undefined, omeMeetEntryId: selectedOmeMeetEntryState.selectedOmeMeetEntryId >= 0 ? selectedOmeMeetEntryState.selectedOmeMeetEntryId : undefined, omeFeeTypeId: INDIVIDUAL_INTL_ATHLETE_OME_FEE_TYPE_ID, amount: formState.costPerIndividualInternationalAthlete }] : []),
            ...(formState.costPerInternationalStaff !== '' && formState.showCostPerInternationalStaff === true ? [{ omeMeetPriceId: updatedOmeMeetEntry.omeMeetEntry[selectedOmeMeetEntryObjIndex].omeMeetPrice?.find(x => x.omeFeeTypeId === INTL_STAFF_OME_FEE_TYPE_ID)?.omeMeetPriceId || undefined, omeMeetEntryId: selectedOmeMeetEntryState.selectedOmeMeetEntryId >= 0 ? selectedOmeMeetEntryState.selectedOmeMeetEntryId : undefined, omeFeeTypeId: INTL_STAFF_OME_FEE_TYPE_ID, amount: formState.costPerInternationalStaff }] : []),
            ...(formState.costPerRelayOnlySwimmer !== '' && formState.showCostPerRelayOnlySwimmer === true ? [{ omeMeetPriceId: updatedOmeMeetEntry.omeMeetEntry[selectedOmeMeetEntryObjIndex].omeMeetPrice?.find(x => x.omeFeeTypeId === RELAY_ONLY_SWIMMER_OME_FEE_TYPE_ID)?.omeMeetPriceId || undefined, omeMeetEntryId: selectedOmeMeetEntryState.selectedOmeMeetEntryId >= 0 ? selectedOmeMeetEntryState.selectedOmeMeetEntryId : undefined, omeFeeTypeId: RELAY_ONLY_SWIMMER_OME_FEE_TYPE_ID, amount: formState.costPerRelayOnlySwimmer }] : []),
            ...(formState.costPerAthleteIndividualEventEntry !== '' ? [{ omeMeetPriceId: updatedOmeMeetEntry.omeMeetEntry[selectedOmeMeetEntryObjIndex].omeMeetPrice?.find(x => x.omeFeeTypeId === ATHLETE_INDIVIDUAL_EVENT_ENTRY_OME_FEE_TYPE_ID)?.omeMeetPriceId || undefined, omeMeetEntryId: selectedOmeMeetEntryState.selectedOmeMeetEntryId >= 0 ? selectedOmeMeetEntryState.selectedOmeMeetEntryId : undefined, omeFeeTypeId: ATHLETE_INDIVIDUAL_EVENT_ENTRY_OME_FEE_TYPE_ID, amount: formState.costPerAthleteIndividualEventEntry }] : []),
            ...(formState.costPerAthleteIndividualBonusEventEntry !== '' && formState.showCostPerAthleteIndividualBonusEventEntry === true ? [{ omeMeetPriceId: updatedOmeMeetEntry.omeMeetEntry[selectedOmeMeetEntryObjIndex].omeMeetPrice?.find(x => x.omeFeeTypeId === ATHLETE_INDIVIDUAL_BONUS_EVENT_ENTRY_OME_FEE_TYPE_ID)?.omeMeetPriceId || undefined, omeMeetEntryId: selectedOmeMeetEntryState.selectedOmeMeetEntryId >= 0 ? selectedOmeMeetEntryState.selectedOmeMeetEntryId : undefined, omeFeeTypeId: ATHLETE_INDIVIDUAL_BONUS_EVENT_ENTRY_OME_FEE_TYPE_ID, amount: formState.costPerAthleteIndividualBonusEventEntry }] : []),
            ...(formState.costPerRelayEventEntry !== '' && formState.showCostPerRelayEventEntry === true ? [{ omeMeetPriceId: updatedOmeMeetEntry.omeMeetEntry[selectedOmeMeetEntryObjIndex].omeMeetPrice?.find(x => x.omeFeeTypeId === RELAY_EVENT_ENTRY_OME_FEE_TYPE_ID)?.omeMeetPriceId || undefined, omeMeetEntryId: selectedOmeMeetEntryState.selectedOmeMeetEntryId >= 0 ? selectedOmeMeetEntryState.selectedOmeMeetEntryId : undefined, omeFeeTypeId: RELAY_EVENT_ENTRY_OME_FEE_TYPE_ID, amount: formState.costPerRelayEventEntry }] : [])
          ]
        }
      }
    }

    return updatedOmeMeetEntry;
  }

  function issueWithMaxNumberOfAthletesForMeet(updatedOmeMeetEntry) {
    const maxNumberOfAthletesForOldMeetArray = omeMeetEntryState?.objData?.omeMeetEntry?.map((x) => x.maxNumberOfAthletesForMeet)?.filter((x) => x !== null);
    const highestOldMaxNumberOfAthletesForMeet = maxNumberOfAthletesForOldMeetArray?.length > 0 ? Math.max(...maxNumberOfAthletesForOldMeetArray) : undefined
    const selectedOldOmeMeetEntryObj = omeMeetEntryState?.objData?.omeMeetEntry?.find(x => x.omeMeetEntryId === selectedOmeMeetEntryState.selectedOmeMeetEntryId);

    const maxNumberOfAthletesForUpdatedMeetArray = updatedOmeMeetEntry?.omeMeetEntry?.map((x) => x.maxNumberOfAthletesForMeet)?.filter((x) => x !== null);
    const highestUpdatedMaxNumberOfAthletesForMeet = maxNumberOfAthletesForUpdatedMeetArray?.length > 0 ? Math.max(...maxNumberOfAthletesForUpdatedMeetArray) : undefined
    const selectedUpdatedOmeMeetEntryObj = selectedOmeMeetEntryState.selectedOmeMeetEntryId >= 0 ? updatedOmeMeetEntry?.omeMeetEntry?.find(x => x.omeMeetEntryId === selectedOmeMeetEntryState.selectedOmeMeetEntryId) : updatedOmeMeetEntry?.omeMeetEntry[updatedOmeMeetEntry?.omeMeetEntry.length - 1];

    const didThisOmeEntryUsedToHaveTheHighestMaxNumberOfAthletesForMeet = ToIntIfInt(selectedOldOmeMeetEntryObj?.maxNumberOfAthletesForMeet?.toString().replace(',', '')) === highestOldMaxNumberOfAthletesForMeet;
    const doesThisOmeEntryNowHaveTheHighestMaxNumberOfAthletesForMeet = ToIntIfInt(selectedUpdatedOmeMeetEntryObj?.maxNumberOfAthletesForMeet?.toString().replace(',', '')) === highestUpdatedMaxNumberOfAthletesForMeet;
  
    if (omeMeetState.objData.omeMeetCoachPass !== undefined &&
      omeMeetState.objData.omeMeetCoachPass?.length > 0 &&
      highestOldMaxNumberOfAthletesForMeet !== undefined &&
      highestUpdatedMaxNumberOfAthletesForMeet !== undefined &&
      didThisOmeEntryUsedToHaveTheHighestMaxNumberOfAthletesForMeet === true &&
      doesThisOmeEntryNowHaveTheHighestMaxNumberOfAthletesForMeet === false) {
      for (let i = 0; i < omeMeetState.objData.omeMeetCoachPass?.length; i++) {
        let maxSwimmersNum = parseInt(omeMeetState.objData.omeMeetCoachPass[i].maxSwimmers);
        if (maxSwimmersNum > highestUpdatedMaxNumberOfAthletesForMeet) {
          return {
            issue: true,
            highestOldMaxNumberOfAthletesForMeet: highestOldMaxNumberOfAthletesForMeet,
            highestUpdatedMaxNumberOfAthletesForMeet: highestUpdatedMaxNumberOfAthletesForMeet,
            errorMessage: `The previous Max Number of Athletes For Meet (${highestOldMaxNumberOfAthletesForMeet}) used to be the highest Max Number of Athletes For Meet accross all OME Meets. By changing this to ${formState.maxNumberOfAthletesForMeet.toString().replace(',', '') === '' ? 'be blank' : formState.maxNumberOfAthletesForMeet.toString().replace(',', '')}, the new highest Max Number of Athletes For Meet across all OME Meets is ${highestUpdatedMaxNumberOfAthletesForMeet}. This new highest Max Number of Athletes For Meet is less than at least one Deck Pass Rule's Max Number of Athletes, which is not allowed. In order to save the provided Max Number of Athletes For Meet, please go to the Eligibility page and delete any Deck Pass Rule with a Max Number of Athletes greater than ${highestUpdatedMaxNumberOfAthletesForMeet}.`
          };
        }
      }
    }

    if (omeMeetState.objData.omeMeetCoachPass !== undefined &&
      omeMeetState.objData.omeMeetCoachPass?.length > 0 &&
      highestUpdatedMaxNumberOfAthletesForMeet !== undefined &&
      doesThisOmeEntryNowHaveTheHighestMaxNumberOfAthletesForMeet === true) {
      for (let i = 0; i < omeMeetState.objData.omeMeetCoachPass?.length; i++) {
        let maxSwimmersNum = parseInt(omeMeetState.objData.omeMeetCoachPass[i].maxSwimmers);
        if (maxSwimmersNum > highestUpdatedMaxNumberOfAthletesForMeet) {
          return {
            issue: true,
            highestOldMaxNumberOfAthletesForMeet: highestOldMaxNumberOfAthletesForMeet,
            highestUpdatedMaxNumberOfAthletesForMeet: highestUpdatedMaxNumberOfAthletesForMeet,
            errorMessage: `The provided Max Number of Athletes For Meet (${highestUpdatedMaxNumberOfAthletesForMeet}) is the highest Max Number of Athletes For Meet across all OME Meets. The highest Max Number of Athletes For Meet cannot be less than any Deck Pass Rule's Max Number of Athletes. In order to save the provided Max Number of Athletes For Meet, please go to the Eligibility page and delete any Deck Pass Rule with a Max Number of Athletes greater than ${highestUpdatedMaxNumberOfAthletesForMeet}.`
          };
        }
      }
    }

    return {
      issue: false,
      highestOldMaxNumberOfAthletesForMeet: highestOldMaxNumberOfAthletesForMeet,
      highestUpdatedMaxNumberOfAthletesForMeet: highestUpdatedMaxNumberOfAthletesForMeet,
      errorMessage: ''
    };
  }

  function issueWithMaxNumberOfAthletesCountsTowardsMainOmeMeet(updatedOmeMeetEntry) {
    const selectedUpdatedOmeMeetEntryObj = selectedOmeMeetEntryState.selectedOmeMeetEntryId >= 0 ? updatedOmeMeetEntry?.omeMeetEntry?.find(x => x.omeMeetEntryId === selectedOmeMeetEntryState.selectedOmeMeetEntryId) : updatedOmeMeetEntry?.omeMeetEntry[updatedOmeMeetEntry?.omeMeetEntry.length - 1];
    const selectedUpdatedOmeMeetEntryObjIndex = selectedOmeMeetEntryState.selectedOmeMeetEntryId >= 0 ? updatedOmeMeetEntry?.omeMeetEntry?.findIndex(x => x.omeMeetEntryId === selectedOmeMeetEntryState.selectedOmeMeetEntryId) : updatedOmeMeetEntry?.omeMeetEntry.length - 1;

    //If the main entry maxNumberOfAthletesForMeet is not greater than the non-main entry maxNumberOfAthletesForMeet, throw an error
    if (selectedUpdatedOmeMeetEntryObj.isMainEntry === true) {
      for (let i = 0; i < updatedOmeMeetEntry?.omeMeetEntry?.length; i++) {
        if (i !== selectedUpdatedOmeMeetEntryObjIndex) {
          if (updatedOmeMeetEntry.omeMeetEntry[i]?.maxNumberOfAthletesCountsTowardsMainOmeMeet === true) {
            if (ToIntIfInt(updatedOmeMeetEntry.omeMeetEntry[i]?.maxNumberOfAthletesForMeet) >= ToIntIfInt(selectedUpdatedOmeMeetEntryObj.maxNumberOfAthletesForMeet)) {
              const maxNumberOfAthletesForOldMeetArray = omeMeetEntryState?.objData?.omeMeetEntry?.filter((x) => x.maxNumberOfAthletesCountsTowardsMainOmeMeet === true && x.isMainEntry === false)?.map((x) => x.maxNumberOfAthletesForMeet)?.filter((x) => x !== null);
              const highestNonMainEntryMaxNumberOfAthletesForMeet = maxNumberOfAthletesForOldMeetArray?.length > 0 ? Math.max(...maxNumberOfAthletesForOldMeetArray) : undefined
              return {
                issue: true,
                errorMessage: `The provided Max Number Of Athletes For Meet is not allowed. A Main OME Meet's Max Number of Athletes For Meet must be greater than any Non-Main OME Meet's Max Number Of Athletes (when the Non-Main OME Meet's 'Count Max # of Athletes towards Main OME Meet Cap?' switch is set to 'Yes'). Please provide a Max Number of Athletes For Meet value greater than ${highestNonMainEntryMaxNumberOfAthletesForMeet}, or navigate to the Non-Main OME Meet(s) and set the 'Count Max # of Athletes towards Main OME Meet Cap?' switch to 'No'.`
              };
            }
          }
        }
      }
    }
    else {
      //If the non-main entry maxNumberOfAthletesForMeet is greater than the main entry maxNumberOfAthletesForMeet, throw an error
      const mainEntryMaxNumberOfAthletesForMeet = updatedOmeMeetEntry?.omeMeetEntry?.find((x) => x.isMainEntry === true)?.maxNumberOfAthletesForMeet;
      if (selectedUpdatedOmeMeetEntryObj.maxNumberOfAthletesCountsTowardsMainOmeMeet === true &&
        selectedUpdatedOmeMeetEntryObj.maxNumberOfAthletesForMeet && ToIntIfInt(selectedUpdatedOmeMeetEntryObj.maxNumberOfAthletesForMeet) >= 0 &&
        mainEntryMaxNumberOfAthletesForMeet && ToIntIfInt(mainEntryMaxNumberOfAthletesForMeet) >= 0) {
        if (ToIntIfInt(selectedUpdatedOmeMeetEntryObj.maxNumberOfAthletesForMeet) >= ToIntIfInt(mainEntryMaxNumberOfAthletesForMeet)) {
          return {
            issue: true,
            errorMessage: `The provided Max Number Of Athletes For Meet is not allowed. A Non-Main OME Meet's Max Number Of Athletes For Meet (when the Non-Main OME Meet's 'Count Max # of Athletes towards Main OME Meet Cap?' switch is set to 'Yes') must be less than the Main OME Meet's Max Number of Athletes For Meet (${mainEntryMaxNumberOfAthletesForMeet}). Please provide a Max Number of Athletes For Meet value less than ${mainEntryMaxNumberOfAthletesForMeet}, or set the 'Count Max # of Athletes towards Main OME Meet Cap?' switch to 'No'.`
          };
        }
      }
    }

    return {
      issue: false,
      errorMessage: ''
    };
  }

  function issueWithOmeMeetName(updatedOmeMeetEntry) {
    const selectedUpdatedOmeMeetEntryObj = selectedOmeMeetEntryState.selectedOmeMeetEntryId >= 0 ? updatedOmeMeetEntry?.omeMeetEntry?.find(x => x.omeMeetEntryId === selectedOmeMeetEntryState.selectedOmeMeetEntryId) : updatedOmeMeetEntry?.omeMeetEntry[updatedOmeMeetEntry?.omeMeetEntry.length - 1];
    const selectedUpdatedOmeMeetEntryObjIndex = selectedOmeMeetEntryState.selectedOmeMeetEntryId >= 0 ? updatedOmeMeetEntry?.omeMeetEntry?.findIndex(x => x.omeMeetEntryId === selectedOmeMeetEntryState.selectedOmeMeetEntryId) : updatedOmeMeetEntry?.omeMeetEntry.length - 1;

    for (let i = 0; i < updatedOmeMeetEntry?.omeMeetEntry?.length; i++) {
      if (i !== selectedUpdatedOmeMeetEntryObjIndex) {
        if (selectedUpdatedOmeMeetEntryObj.omeMeetName === updatedOmeMeetEntry.omeMeetEntry[i]?.omeMeetName) {
          return {
            issue: true,
            errorMessage: `The provided OME Meet Name is already in use. OME Meet Names must be different.`
          };
        }
      }
    }

    return {
      issue: false,
      errorMessage: ''
    };
  }

  function submitFormCallback(formState) {
    const updatedOmeEntryInvitationArray =
      formState.orgUnit?.map(x => {
        return (
          {
            orgUnitId: x.id
          }
        )
      });

    //Edit OmeMeetEntry
    if (selectedOmeMeetEntryState.selectedOmeMeetEntryId >= 0) {
      let updatedOmeMeetEntry = JSON.parse(JSON.stringify(omeMeetEntryState.objData));
      updatedOmeMeetEntry = editOmeMeetEntryCopy(updatedOmeMeetEntry, formState, selectedOmeMeetEntryState.selectedOmeMeetEntryId);

      let error = '';
      const issueWithMaxNumberOfAthletesForMeetReturn = issueWithMaxNumberOfAthletesForMeet(updatedOmeMeetEntry);
      if (issueWithMaxNumberOfAthletesForMeetReturn?.issue === true) {
        error = issueWithMaxNumberOfAthletesForMeetReturn?.errorMessage;
      }
      const issueWithMaxNumberOfAthletesCountsTowardsMainOmeMeetReturn = issueWithMaxNumberOfAthletesCountsTowardsMainOmeMeet(updatedOmeMeetEntry);
      if (issueWithMaxNumberOfAthletesCountsTowardsMainOmeMeetReturn?.issue === true) {
        error = error ? error + `\n\n ${issueWithMaxNumberOfAthletesCountsTowardsMainOmeMeetReturn?.errorMessage}` : issueWithMaxNumberOfAthletesCountsTowardsMainOmeMeetReturn?.errorMessage;
      }
      const issueWithOmeMeetNameReturn = issueWithOmeMeetName(updatedOmeMeetEntry);
      if (issueWithOmeMeetNameReturn?.issue === true) {
        error = error ? error + `\n\n ${issueWithOmeMeetNameReturn?.errorMessage}` : issueWithOmeMeetNameReturn?.errorMessage;
      }

      if (error !== '') {
        setErrors({ ...errorState, error: error });
      }
      else {
        putOmeMeetEntry(omeMeetEntryState.objData.omeMeetId, updatedOmeMeetEntry);

        //Check to see if the invitations are different and need to be saved
        let needToUpdateInvitiations = false;
        const selectedOmeMeetEntryObj = omeMeetEntryState.objData?.omeMeetEntry?.find(x => x.omeMeetEntryId === selectedOmeMeetEntryState.selectedOmeMeetEntryId);

        if (selectedOmeMeetEntryObj.omeEntryInvitation?.length !== updatedOmeEntryInvitationArray.length) {
          needToUpdateInvitiations = true;
        }
        else {
          for (let i = 0; i < updatedOmeEntryInvitationArray.length; i++) {
            let existingOrgUnitId = selectedOmeMeetEntryObj.omeEntryInvitation?.find(x => x.orgUnitId === updatedOmeEntryInvitationArray[i].orgUnitId);
            if (!existingOrgUnitId) {
              needToUpdateInvitiations = true;
            }
          }
        }

        if (needToUpdateInvitiations === true) {
          setState({ ...state, updatedOmeEntryInvitationArray: updatedOmeEntryInvitationArray, tryPostOmeMeetEntryInvitations: true });
        }
        else {
          setState({ ...state, tryRedirect: true });
        }
      }
    }
    //Add OmeMeetEntry
    else {
      const updatedOmeMeetEntry = JSON.parse(JSON.stringify(omeMeetEntryState.objData));
      const todaysDate = getTodaysDate();
      updatedOmeMeetEntry.omeMeetEntry.push({
        omeMeetId: omeMeetEntryState.objData.omeMeetId,
        isMainEntry: false,
        omeMeetName: formState.omeMeetName.trim() || '',
        meetEntryStartDate: formState.meetEntryStartDate,
        meetEntryEndDate: formState.meetEntryEndDate,
        meetEntryChangeDeadline: formState.meetEntryChangeDeadline,
        meetCreationDate: todaysDate,
        maxNumberOfAthletesForMeet: formState.maxNumberOfAthletesForMeet.toString().replace(',', '') || null,
        maxNumberOfCapExemptAthletesForMeet: null,
        maxNumberOfAthletesCountsTowardsMainOmeMeet: formState.maxNumberOfAthletesCountsTowardsMainOmeMeet ?? null,
        includeRelayOnlySwimmersInMaxNumber: formState.relayEvents === true && formState.allowRelayOnlySwimmer === true ? (formState.includeRelayOnlySwimmersInMaxNumber ?? null) : null,
        nationalTeamOnly: formState.nationalTeamOnly ?? null,
        status: HostRegistrationConstants.COMPLETE_STATUS,
        omeMeetQualificationDate: [{
          omeMeetEntryId: omeMeetEntryState.objData.omeMeetId,
          timeStandardsQualifyingStartDate: formState.hasTimeStandards === true ? (formState.hasQualifyingWindow === true ?
            ((formState.timeStandardsQualifyingStartDate !== Constants.BLANK_DATE_STRING ? formState.timeStandardsQualifyingStartDate : null)) : Constants.DEFAULT_MIN_DATE)
            : null,
          timeStandardsQualifyingEndDate: formState.hasTimeStandards === true ? (formState.hasQualifyingWindow === true ?
            ((formState.timeStandardsQualifyingEndDate !== Constants.BLANK_DATE_STRING ? formState.timeStandardsQualifyingEndDate : null)) :
            formState.meetEntryEndDate.substring(0, 10))
            : null
        }],
        omeEntryInvitation: [],
        omeMeetPrice: [
          ...(formState.costPerTeam !== '' ? [{ omeMeetPriceId: undefined, omeMeetEntryId: selectedOmeMeetEntryState.selectedOmeMeetEntryId >= 0 ? selectedOmeMeetEntryState.selectedOmeMeetEntryId : undefined, omeFeeTypeId: TEAM_OME_FEE_TYPE_ID, amount: formState.costPerTeam }] : []),
          ...(formState.costPerCredential !== '' ? [{ omeMeetPriceId: undefined, omeMeetEntryId: selectedOmeMeetEntryState.selectedOmeMeetEntryId >= 0 ? selectedOmeMeetEntryState.selectedOmeMeetEntryId : undefined, omeFeeTypeId: CREDENTIAL_OME_FEE_TYPE_ID, amount: formState.costPerCredential }] : []),
          ...(formState.costPerIndividualAthlete !== '' ? [{ omeMeetPriceId: undefined, omeMeetEntryId: selectedOmeMeetEntryState.selectedOmeMeetEntryId >= 0 ? selectedOmeMeetEntryState.selectedOmeMeetEntryId : undefined, omeFeeTypeId: INDIVIDUAL_ATHLETE_OME_FEE_TYPE_ID, amount: formState.costPerIndividualAthlete }] : []),
          ...(formState.costPerIndividualInternationalAthlete !== '' && formState.showCostPerIndividualInternationalAthlete === true ? [{ omeMeetPriceId: undefined, omeMeetEntryId: selectedOmeMeetEntryState.selectedOmeMeetEntryId >= 0 ? selectedOmeMeetEntryState.selectedOmeMeetEntryId : undefined, omeFeeTypeId: INDIVIDUAL_INTL_ATHLETE_OME_FEE_TYPE_ID, amount: formState.costPerIndividualInternationalAthlete }] : []),
          ...(formState.costPerInternationalStaff !== '' && formState.showCostPerInternationalStaff === true ? [{ omeMeetPriceId: undefined, omeFeeTypeId: INTL_STAFF_OME_FEE_TYPE_ID, omeMeetEntryId: selectedOmeMeetEntryState.selectedOmeMeetEntryId >= 0 ? selectedOmeMeetEntryState.selectedOmeMeetEntryId : undefined, amount: formState.costPerInternationalStaff }] : []),
          ...(formState.costPerRelayOnlySwimmer !== '' && formState.showCostPerRelayOnlySwimmer === true ? [{ omeMeetPriceId: undefined, omeMeetEntryId: selectedOmeMeetEntryState.selectedOmeMeetEntryId >= 0 ? selectedOmeMeetEntryState.selectedOmeMeetEntryId : undefined, omeFeeTypeId: RELAY_ONLY_SWIMMER_OME_FEE_TYPE_ID, amount: formState.costPerRelayOnlySwimmer }] : []),
          ...(formState.costPerAthleteIndividualEventEntry !== '' ? [{ omeMeetPriceId: undefined, omeMeetEntryId: selectedOmeMeetEntryState.selectedOmeMeetEntryId >= 0 ? selectedOmeMeetEntryState.selectedOmeMeetEntryId : undefined, omeFeeTypeId: ATHLETE_INDIVIDUAL_EVENT_ENTRY_OME_FEE_TYPE_ID, amount: formState.costPerAthleteIndividualEventEntry }] : []),
          ...(formState.costPerAthleteIndividualBonusEventEntry !== '' && formState.showCostPerAthleteIndividualBonusEventEntry === true ? [{ omeMeetPriceId: undefined, omeMeetEntryId: selectedOmeMeetEntryState.selectedOmeMeetEntryId >= 0 ? selectedOmeMeetEntryState.selectedOmeMeetEntryId : undefined, omeFeeTypeId: ATHLETE_INDIVIDUAL_BONUS_EVENT_ENTRY_OME_FEE_TYPE_ID, amount: formState.costPerAthleteIndividualBonusEventEntry }] : []),
          ...(formState.costPerRelayEventEntry !== '' && formState.showCostPerRelayEventEntry === true ? [{ omeMeetPriceId: undefined, omeMeetEntryId: selectedOmeMeetEntryState.selectedOmeMeetEntryId >= 0 ? selectedOmeMeetEntryState.selectedOmeMeetEntryId : undefined, omeFeeTypeId: RELAY_EVENT_ENTRY_OME_FEE_TYPE_ID, amount: formState.costPerRelayEventEntry }] : [])
        ]
      });

      let error = '';
      const issueWithMaxNumberOfAthletesForMeetReturn = issueWithMaxNumberOfAthletesForMeet(updatedOmeMeetEntry);
      if (issueWithMaxNumberOfAthletesForMeetReturn?.issue === true) {
        error = issueWithMaxNumberOfAthletesForMeetReturn?.errorMessage;
      }
      const issueWithMaxNumberOfAthletesCountsTowardsMainOmeMeetReturn = issueWithMaxNumberOfAthletesCountsTowardsMainOmeMeet(updatedOmeMeetEntry);
      if (issueWithMaxNumberOfAthletesCountsTowardsMainOmeMeetReturn?.issue === true) {
        error = error ? error + `\n\n ${issueWithMaxNumberOfAthletesCountsTowardsMainOmeMeetReturn?.errorMessage}` : issueWithMaxNumberOfAthletesCountsTowardsMainOmeMeetReturn?.errorMessage;
      }
      const issueWithOmeMeetNameReturn = issueWithOmeMeetName(updatedOmeMeetEntry);
      if (issueWithOmeMeetNameReturn?.issue === true) {
        error = error ? error + `\n\n ${issueWithOmeMeetNameReturn?.errorMessage}` : issueWithOmeMeetNameReturn?.errorMessage;
      }
      if (error !== '') {
        setErrors({ ...errorState, error: error });
      }
      else {
        putOmeMeetEntry(omeMeetEntryState.objData.omeMeetId, updatedOmeMeetEntry);
        setState({ ...state, prePutOmeMeetEntryStateObjData: omeMeetEntryState.objData, updatedOmeEntryInvitationArray: updatedOmeEntryInvitationArray, tryPostOmeMeetEntryInvitations: true });
      }
    }
  }

  function formStateHasBeenSaved(formState) {
    //compare formState to omeMeetState to see if formState has been saved
    //Edit
    if (selectedOmeMeetEntryState.selectedOmeMeetEntryId >= 0) {
      const selectedOmeMeetEntryObj = omeMeetEntryState.objData?.omeMeetEntry?.find(x => x.omeMeetEntryId === selectedOmeMeetEntryState.selectedOmeMeetEntryId);

      if (formState?.omeMeetName !== selectedOmeMeetEntryObj?.omeMeetName
        && (formState?.omeMeetName !== '' || selectedOmeMeetEntryObj?.omeMeetName !== null)) {
        return false;
      }
      if (formState?.meetEntryStartDate !== formatDateTime(selectedOmeMeetEntryObj?.meetEntryStartDate)
        && (formState?.meetEntryStartDate !== Constants.BLANK_DATETIME_STRING || selectedOmeMeetEntryObj?.meetEntryStartDate !== null)) {
        return false;
      }
      if (formState?.meetEntryEndDate !== formatDateTime(selectedOmeMeetEntryObj?.meetEntryEndDate)
        && (formState?.meetEntryEndDate !== Constants.BLANK_DATETIME_STRING || selectedOmeMeetEntryObj?.meetEntryEndDate !== null)) {
        return false;
      }
      if (formState?.meetEntryChangeDeadline !== formatDateTime(selectedOmeMeetEntryObj?.meetEntryChangeDeadline)
        && (formState?.meetEntryChangeDeadline !== Constants.BLANK_DATETIME_STRING || selectedOmeMeetEntryObj?.meetEntryChangeDeadline !== null)) {
        return false;
      }
      if (ToIntIfInt(formState?.maxNumberOfAthletesForMeet.toString().replace(',', '')) !== selectedOmeMeetEntryObj?.maxNumberOfAthletesForMeet
        && (ToIntIfInt(formState?.maxNumberOfAthletesForMeet.toString().replace(',', '')) !== '' || selectedOmeMeetEntryObj?.maxNumberOfAthletesForMeet !== null)) {
        return false;
      }
      if (formState?.maxNumberOfAthletesCountsTowardsMainOmeMeet !== selectedOmeMeetEntryObj?.maxNumberOfAthletesCountsTowardsMainOmeMeet
        && (formState?.maxNumberOfAthletesCountsTowardsMainOmeMeet !== '' || selectedOmeMeetEntryObj?.maxNumberOfAthletesCountsTowardsMainOmeMeet !== null)) {
        return false;
      }
      if (formState?.includeRelayOnlySwimmersInMaxNumber !== selectedOmeMeetEntryObj?.includeRelayOnlySwimmersInMaxNumber
        && (formState?.includeRelayOnlySwimmersInMaxNumber !== '' || selectedOmeMeetEntryObj?.includeRelayOnlySwimmersInMaxNumber !== null)) {
        return false;
      }
      if (formState?.nationalTeamOnly !== selectedOmeMeetEntryObj?.nationalTeamOnly
        && (formState?.nationalTeamOnly !== '' || selectedOmeMeetEntryObj?.nationalTeamOnly !== null)) {
        return false;
      }

      if (selectedOmeMeetEntryObj?.omeEntryInvitation && selectedOmeMeetEntryObj?.omeEntryInvitation?.length > 0) {
        const orgUnitIdArray = selectedOmeMeetEntryObj?.omeEntryInvitation.map(x => { return { id: x.orgUnitId } });
        const orgUnitIdArrayValues = [];
        for (let i = 0; i < orgUnitIdArray.length; i++) {
          orgUnitIdArrayValues.push(orgUnitIdArray[i].id);
        }

        for (let i = 0; i < orgUnitIdArrayValues.length; i++) {
          let match = formState.orgUnit?.find(x => x.id === orgUnitIdArrayValues[i]);
          if (!match) {
            return false;
          }
        }

        for (let i = 0; i < formState.orgUnit?.length; i++) {
          let match = orgUnitIdArrayValues?.find(x => x === formState.orgUnit[i].id);
          if (!match) {
            return false;
          }
        }
      }

      const hasQualifyingWindow = formState?.hasTimeStandards === true ? ((formState.timeStandardsQualifyingStartDate && formatDate(formState.timeStandardsQualifyingStartDate) !== Constants.DEFAULT_MIN_DATE) ||
        (formState.timeStandardsQualifyingEndDate && formatDate(formState.timeStandardsQualifyingEndDate) !== formatDateTime(selectedOmeMeetEntryObj?.meetEntryEndDate)?.substring(0, 10)) ?
        true : false) : null;

      if (formState?.hasQualifyingWindow !== hasQualifyingWindow
        && (formState?.hasQualifyingWindow !== '' || hasQualifyingWindow !== null)) {
        return false;
      }
      if (formState?.timeStandardsQualifyingStartDate !== formatDate(selectedOmeMeetEntryObj?.omeMeetQualificationDate[0]?.timeStandardsQualifyingStartDate)
        && (formState?.timeStandardsQualifyingStartDate !== Constants.BLANK_DATE_STRING || selectedOmeMeetEntryObj?.omeMeetQualificationDate[0]?.timeStandardsQualifyingStartDate !== null)) {
        return false;
      }
      if (formState?.timeStandardsQualifyingEndDate !== formatDate(selectedOmeMeetEntryObj?.omeMeetQualificationDate[0]?.timeStandardsQualifyingEndDate)
        && (formState?.timeStandardsQualifyingEndDate !== Constants.BLANK_DATE_STRING || selectedOmeMeetEntryObj?.omeMeetQualificationDate[0]?.timeStandardsQualifyingEndDate !== null)) {
        return false;
      }

      let costPerTeam = '';
      let costPerCredential = '';
      let costPerIndividualAthlete = '';
      let costPerIndividualInternationalAthlete = '';
      let costPerInternationalStaff = '';
      let costPerRelayOnlySwimmer = '';
      let costPerAthleteIndividualEventEntry = '';
      let costPerAthleteIndividualBonusEventEntry = '';
      let costPerRelayEventEntry = '';

      //If the fee type exists in omeMeetState, check to see if the formState matches
      for (let i = 0; i < selectedOmeMeetEntryObj?.omeMeetPrice?.length; i++) {
        if (selectedOmeMeetEntryObj.omeMeetPrice[i].omeFeeTypeId === TEAM_OME_FEE_TYPE_ID) {
          costPerTeam = selectedOmeMeetEntryObj.omeMeetPrice[i].amount;
          if (costPerTeam !== formState?.costPerTeam
            && (formState?.costPerTeam !== '' || selectedOmeMeetEntryObj.omeMeetPrice[i].amount !== null)) {
            return false;
          }
        } else if (selectedOmeMeetEntryObj.omeMeetPrice[i].omeFeeTypeId === CREDENTIAL_OME_FEE_TYPE_ID) {
          costPerCredential = selectedOmeMeetEntryObj.omeMeetPrice[i].amount;
          if (costPerCredential !== formState?.costPerCredential
            && (formState?.costPerCredential !== '' || selectedOmeMeetEntryObj.omeMeetPrice[i].amount !== null)) {
            return false;
          }
        } else if (selectedOmeMeetEntryObj.omeMeetPrice[i].omeFeeTypeId === INDIVIDUAL_ATHLETE_OME_FEE_TYPE_ID) {
          costPerIndividualAthlete = selectedOmeMeetEntryObj.omeMeetPrice[i].amount;
          if (costPerIndividualAthlete !== formState?.costPerIndividualAthlete
            && (formState?.costPerIndividualAthlete !== '' || selectedOmeMeetEntryObj.omeMeetPrice[i].amount !== null)) {
            return false;
          }
        } else if (selectedOmeMeetEntryObj.omeMeetPrice[i].omeFeeTypeId === INDIVIDUAL_INTL_ATHLETE_OME_FEE_TYPE_ID) {
          costPerIndividualInternationalAthlete = selectedOmeMeetEntryObj.omeMeetPrice[i].amount;
          if (costPerIndividualInternationalAthlete !== formState?.costPerIndividualInternationalAthlete
            && (formState?.costPerIndividualInternationalAthlete !== '' || selectedOmeMeetEntryObj.omeMeetPrice[i].amount !== null)) {
            return false;
          }
        }
        else if (selectedOmeMeetEntryObj.omeMeetPrice[i].omeFeeTypeId === INTL_STAFF_OME_FEE_TYPE_ID) {
          costPerInternationalStaff = selectedOmeMeetEntryObj.omeMeetPrice[i].amount;
          if (costPerInternationalStaff !== formState?.costPerInternationalStaff
            && (formState?.costPerInternationalStaff !== '' || selectedOmeMeetEntryObj.omeMeetPrice[i].amount !== null)) {
            return false;
          }
        } else if (selectedOmeMeetEntryObj.omeMeetPrice[i].omeFeeTypeId === RELAY_ONLY_SWIMMER_OME_FEE_TYPE_ID) {
          costPerRelayOnlySwimmer = selectedOmeMeetEntryObj.omeMeetPrice[i].amount;
          if (costPerRelayOnlySwimmer !== formState?.costPerRelayOnlySwimmer
            && (formState?.costPerRelayOnlySwimmer !== '' || selectedOmeMeetEntryObj.omeMeetPrice[i].amount !== null)) {
            return false;
          }
        } else if (selectedOmeMeetEntryObj.omeMeetPrice[i].omeFeeTypeId === ATHLETE_INDIVIDUAL_EVENT_ENTRY_OME_FEE_TYPE_ID) {
          costPerAthleteIndividualEventEntry = selectedOmeMeetEntryObj.omeMeetPrice[i].amount;
          if (costPerAthleteIndividualEventEntry !== formState?.costPerAthleteIndividualEventEntry
            && (formState?.costPerAthleteIndividualEventEntry !== '' || selectedOmeMeetEntryObj.omeMeetPrice[i].amount !== null)) {
            return false;
          }
        } else if (selectedOmeMeetEntryObj.omeMeetPrice[i].omeFeeTypeId === ATHLETE_INDIVIDUAL_BONUS_EVENT_ENTRY_OME_FEE_TYPE_ID) {
          costPerAthleteIndividualBonusEventEntry = selectedOmeMeetEntryObj.omeMeetPrice[i].amount;
          if (costPerAthleteIndividualBonusEventEntry !== formState?.costPerAthleteIndividualBonusEventEntry
            && (formState?.costPerAthleteIndividualBonusEventEntry !== '' || selectedOmeMeetEntryObj.omeMeetPrice[i].amount !== null)) {
            return false;
          }
        } else if (selectedOmeMeetEntryObj.omeMeetPrice[i].omeFeeTypeId === RELAY_EVENT_ENTRY_OME_FEE_TYPE_ID) {
          costPerRelayEventEntry = selectedOmeMeetEntryObj.omeMeetPrice[i].amount;
          if (costPerRelayEventEntry !== formState?.costPerRelayEventEntry
            && (formState?.costPerRelayEventEntry !== '' || selectedOmeMeetEntryObj.omeMeetPrice[i].amount !== null)) {
            return false;
          }
        }
      }

      //If the fee type doesn't exist in omeMeetEntryState, check to see if the formState matches
      if (costPerTeam === '' && formState?.costPerTeam !== '') {
        return false;
      }
      if (costPerCredential === '' && formState?.costPerCredential !== '') {
        return false;
      }
      if (costPerIndividualAthlete === '' && formState?.costPerIndividualAthlete !== '') {
        return false;
      }
      if (costPerIndividualInternationalAthlete === '' && formState?.costPerIndividualInternationalAthlete !== '') {
        return false;
      }
      if (costPerInternationalStaff === '' && formState?.costPerInternationalStaff !== '') {
        return false;
      }
      if (costPerRelayOnlySwimmer === '' && formState?.costPerRelayOnlySwimmer !== '') {
        return false;
      }
      if (costPerAthleteIndividualEventEntry === '' && formState?.costPerAthleteIndividualEventEntry !== '') {
        return false;
      }
      if (costPerAthleteIndividualBonusEventEntry === '' && formState?.costPerAthleteIndividualBonusEventEntry !== '') {
        return false;
      }
      if (costPerRelayEventEntry === '' && formState?.costPerRelayEventEntry !== '') {
        return false;
      }
    }
    //Add
    else {
      if (isDirty === true) {
        return false;
      }
      return true;
    }

    return true;
  }

  useEffect(() => {
    if (location.state && location.state?.omeMeetEntryId >= 0) {
      setSelectedOmeMeetEntryState({ ...selectedOmeMeetEntryState, selectedOmeMeetEntryId: location.state.omeMeetEntryId, initialLoadComplete: true });
    }
    else {
      setSelectedOmeMeetEntryState({ ...selectedOmeMeetEntryState, initialLoadComplete: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (orgUnitState.isArrayLoaded === false && orgUnitState.isArrayLoading === false) {
      getOrgUnits();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgUnitState]);

  useEffect(() => {
    let selectedOrgUnitNames = [];
    if (Array.isArray(formState.orgUnit) === true && formState.orgUnit.length > 0) {
      formState.orgUnit.forEach(x => selectedOrgUnitNames.push(x.name));
    }
    const selectedOrgUnitNamesString = selectedOrgUnitNames.join(', ');
    setState({
      ...state,
      organizationInvitations: selectedOrgUnitNamesString
    })
    const hasFINAInvitation = selectionsContainFinaOrgUnit(formState);
    setFormState({
      ...formState,
      showCostPerIndividualInternationalAthlete: hasFINAInvitation,
      showCostPerInternationalStaff: hasFINAInvitation
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formState.orgUnit]);

  useEffect(() => {
    if (orgUnitState.isArrayLoaded === true && Object.keys(omeMeetState.objData).length > 0 && selectedOmeMeetEntryState.initialLoadComplete === true &&
      omeMeetEntryState.isObjLoaded === true && omeMeetEntryState.isObjLoading === false && omeMeetEntryState.isSaving === false &&
      omeMeetEntryInvitationsState.isSaving === false && dateTimeState.isObjLoaded === true) {

      const hasBonusEvents = omeMeetState.objData.omeMeetEligibility[0]?.bonusEvents ?? '';
      const hasRelayEvents = omeMeetState.objData.omeMeetEligibility[0]?.relayEvents ?? '';
      const allowRelayOnlySwimmer = omeMeetState.objData.omeMeetEligibility[0]?.allowRelayOnlySwimmer ?? '';

      let hasQualifyingWindow = '';

      //Set formState from location.state if 'Back' button was clicked on pricing additional purchases detail page
      if (location.state && location.state?.detailFormState) {

        if (selectedOmeMeetEntryState.selectedOmeMeetEntryId >= 0) {
          //Edit
          const selectedOmeMeetEntryObj = omeMeetEntryState.objData?.omeMeetEntry?.find(x => x.omeMeetEntryId === selectedOmeMeetEntryState.selectedOmeMeetEntryId);
          const additionalPurchaseOnlyArrayData = selectedOmeMeetEntryObj?.omeMeetPrice.filter(x => x.omeFeeType?.isAdditionalPurchase === true);
          setState({ ...state, gridData: additionalPurchaseOnlyArrayData });
        }

        const orgUnitIdArray = location.state?.detailFormState?.orgUnit?.map(x => { return { id: x.id } });
        const orgUnitIdArrayValues = [];
        for (let i = 0; i < orgUnitIdArray?.length; i++) {
          orgUnitIdArrayValues.push(orgUnitIdArray[i].id);
        }
        hasQualifyingWindow = location.state?.detailFormState?.hasQualifyingWindow;

        const hasFINAInvitation = selectionsContainFinaOrgUnit(location.state?.detailFormState);

        setFormData({
          ...formState,
          meetStartDate: omeMeetState.selectedMeet?.startDate ? formatDate(omeMeetState.selectedMeet?.startDate) : Constants.BLANK_DATE_STRING,
          publishDate: omeMeetState.objData?.publishDate ? formatDateTime(omeMeetState.objData?.publishDate) : Constants.BLANK_DATETIME_STRING,
          omeMeetName: location.state?.detailFormState?.omeMeetName || '',
          isMainEntry: location.state?.detailFormState?.isMainEntry ?? false,
          meetEntryStartDate: location.state?.detailFormState?.meetEntryStartDate || Constants.BLANK_DATETIME_STRING,
          meetEntryEndDate: location.state?.detailFormState?.meetEntryEndDate || Constants.BLANK_DATETIME_STRING,
          meetEntryChangeDeadline: location.state?.detailFormState?.meetEntryChangeDeadline || Constants.BLANK_DATETIME_STRING,
          meetEntryStartDateTwelveHourClock: location.state?.detailFormState?.meetEntryStartDate || Constants.BLANK_DATETIME_STRING,
          meetEntryEndDateTwelveHourClock: location.state?.detailFormState?.meetEntryEndDate || Constants.BLANK_DATETIME_STRING,
          meetEntryChangeDeadlineTwelveHourClock: location.state?.detailFormState?.meetEntryChangeDeadline || Constants.BLANK_DATETIME_STRING,
          relayEvents: omeMeetState?.objData?.omeMeetEligibility[0]?.relayEvents ?? '',
          allowRelayOnlySwimmer: omeMeetState?.objData.omeMeetEligibility[0]?.allowRelayOnlySwimmer ?? '',
          maxNumberOfAthletesForMeet: location.state?.detailFormState?.maxNumberOfAthletesForMeet !== null &&
            location.state?.detailFormState?.maxNumberOfAthletesForMeet !== undefined ?
            location.state?.detailFormState?.maxNumberOfAthletesForMeet : '',
          maxNumberOfAthletesCountsTowardsMainOmeMeet: location.state?.detailFormState?.maxNumberOfAthletesCountsTowardsMainOmeMeet ?? '',
          includeRelayOnlySwimmersInMaxNumber: location.state?.detailFormState?.includeRelayOnlySwimmersInMaxNumber ?? '',
          nationalTeamOnly: location.state?.detailFormState?.nationalTeamOnly ?? '',
          orgUnit: orgUnitIdArrayValues?.length > 0 ? HierarchicalDataUtils.GetNameIdPairs(orgUnitState.treeData, orgUnitIdArrayValues) || [] : [],
          hasTimeStandards: omeMeetState.objData?.omeMeetQualification[0]?.hasTimeStandards ?? '',
          hasQualifyingWindow: hasQualifyingWindow,
          timeStandardsQualifyingStartDate: location.state?.detailFormState?.timeStandardsQualifyingStartDate ?
            formatDate(location.state?.detailFormState?.timeStandardsQualifyingStartDate) : Constants.BLANK_DATE_STRING,
          timeStandardsQualifyingEndDate: location.state?.detailFormState?.timeStandardsQualifyingEndDate ?
            formatDate(location.state?.detailFormState?.timeStandardsQualifyingEndDate) : Constants.BLANK_DATE_STRING,
          costPerTeam: location.state?.detailFormState?.costPerTeam !== null && location.state?.detailFormState?.costPerTeam !== undefined ?
            location.state?.detailFormState?.costPerTeam : '',
          costPerCredential: location.state?.detailFormState?.costPerCredential !== null && location.state?.detailFormState?.costPerCredential !== undefined ?
            location.state?.detailFormState?.costPerCredential : '',
          costPerIndividualAthlete: location.state?.detailFormState?.costPerIndividualAthlete !== null && location.state?.detailFormState?.costPerIndividualAthlete !== undefined ?
            location.state?.detailFormState?.costPerIndividualAthlete : '',
          costPerIndividualInternationalAthlete: location.state?.detailFormState?.costPerIndividualInternationalAthlete !== null && location.state?.detailFormState?.costPerIndividualInternationalAthlete !== undefined ?
            location.state?.detailFormState?.costPerIndividualInternationalAthlete : '',
          costPerInternationalStaff: location.state?.detailFormState?.costPerInternationalStaff !== null && location.state?.detailFormState?.costPerInternationalStaff !== undefined ?
            location.state?.detailFormState?.costPerInternationalStaff : '',
          costPerRelayOnlySwimmer: location.state?.detailFormState?.costPerRelayOnlySwimmer !== null && location.state?.detailFormState?.costPerRelayOnlySwimmer !== undefined ?
            location.state?.detailFormState?.costPerRelayOnlySwimmer : '',
          costPerAthleteIndividualEventEntry: location.state?.detailFormState?.costPerAthleteIndividualEventEntry !== null && location.state?.detailFormState?.costPerAthleteIndividualEventEntry !== undefined ?
            location.state?.detailFormState?.costPerAthleteIndividualEventEntry : '',
          costPerAthleteIndividualBonusEventEntry: location.state?.detailFormState?.costPerAthleteIndividualBonusEventEntry !== null && location.state?.detailFormState?.costPerAthleteIndividualBonusEventEntry !== undefined ?
            location.state?.detailFormState?.costPerAthleteIndividualBonusEventEntry : '',
          costPerRelayEventEntry: location.state?.detailFormState?.costPerRelayEventEntry !== null && location.state?.detailFormState?.costPerRelayEventEntry !== undefined ?
            location.state?.detailFormState?.costPerRelayEventEntry : '',
          currentCostPerTeam: location.state?.detailFormState?.currentCostPerTeam !== null && location.state?.detailFormState?.currentCostPerTeam !== undefined ?
            location.state?.detailFormState?.currentCostPerTeam : '',
          currentCostPerCredential: location.state?.detailFormState?.currentCostPerCredential !== null && location.state?.detailFormState?.currentCostPerCredential !== undefined ?
            location.state?.detailFormState?.currentCostPerCredential : '',
          currentCostPerIndividualAthlete: location.state?.detailFormState?.currentCostPerIndividualAthlete !== null && location.state?.detailFormState?.currentCostPerIndividualAthlete !== undefined ?
            location.state?.detailFormState?.currentCostPerIndividualAthlete : '',
          currentCostPerIndividualInternationalAthlete: location.state?.detailFormState?.currentCostPerIndividualInternationalAthlete !== null && location.state?.detailFormState?.currentCostPerIndividualInternationalAthlete !== undefined ?
            location.state?.detailFormState?.currentCostPerIndividualInternationalAthlete : '',
          currentCostPerInternationalStaff: location.state?.detailFormState?.currentCostPerInternationalStaff !== null && location.state?.detailFormState?.currentCostPerInternationalStaff !== undefined ?
            location.state?.detailFormState?.currentCostPerInternationalStaff : '',
          currentCostPerRelayOnlySwimmer: location.state?.detailFormState?.currentCostPerRelayOnlySwimmer !== null && location.state?.detailFormState?.currentCostPerRelayOnlySwimmer !== undefined ?
            location.state?.detailFormState?.currentCostPerRelayOnlySwimmer : '',
          currentCostPerAthleteIndividualEventEntry: location.state?.detailFormState?.currentCostPerAthleteIndividualEventEntry !== null && location.state?.detailFormState?.currentCostPerAthleteIndividualEventEntry !== undefined ?
            location.state?.detailFormState?.currentCostPerAthleteIndividualEventEntry : '',
          currentCostPerAthleteIndividualBonusEventEntry: location.state?.detailFormState?.currentCostPerAthleteIndividualBonusEventEntry !== null && location.state?.detailFormState?.currentCostPerAthleteIndividualBonusEventEntry !== undefined ?
            location.state?.detailFormState?.currentCostPerAthleteIndividualBonusEventEntry : '',
          currentCostPerRelayEventEntry: location.state?.detailFormState?.currentCostPerRelayEventEntry !== null && location.state?.detailFormState?.currentCostPerRelayEventEntry !== undefined ?
            location.state?.detailFormState?.currentCostPerRelayEventEntry : '',
          showCostPerIndividualInternationalAthlete: hasFINAInvitation,
          showCostPerInternationalStaff: hasFINAInvitation,
          showCostPerAthleteIndividualBonusEventEntry: hasBonusEvents,
          showCostPerRelayOnlySwimmer: hasRelayEvents === true && allowRelayOnlySwimmer === true ? true : false,
          showCostPerRelayEventEntry: hasRelayEvents
        });

        //If this is a brand new OME Meet, make sure it gets saved
        if (selectedOmeMeetEntryState.selectedOmeMeetEntryId === Constants.DEFAULT_ID) {
          setIsDirty(true);
        }
      }
      else {
        if (selectedOmeMeetEntryState.selectedOmeMeetEntryId >= 0) {
          //Edit
          const selectedOmeMeetEntryObj = omeMeetEntryState.objData?.omeMeetEntry?.find(x => x.omeMeetEntryId === selectedOmeMeetEntryState.selectedOmeMeetEntryId);
          const additionalPurchaseOnlyArrayData = selectedOmeMeetEntryObj?.omeMeetPrice.filter(x => x.omeFeeType?.isAdditionalPurchase === true);
          setState({ ...state, gridData: additionalPurchaseOnlyArrayData });

          //Set formState from omeMeetState and omeMeetEntryState
          const orgUnitIdArray = selectedOmeMeetEntryObj?.omeEntryInvitation?.map(x => { return { id: x.orgUnitId } });
          const orgUnitIdArrayValues = [];
          for (let i = 0; i < orgUnitIdArray?.length; i++) {
            orgUnitIdArrayValues.push(orgUnitIdArray[i].id);
          }

          let savedInvitationsContainFinaOrgUnit = false;
          const finaOrgUnits = orgUnitState.arrayData.find(x => x.id === FINA_ORG_UNIT_ID);

          //Check saved invitations for FINA
          for (let i = 0; i < selectedOmeMeetEntryObj?.omeEntryInvitation?.length; i++) {
            //Check to see if FINA is a saved invitation
            if (selectedOmeMeetEntryObj?.omeEntryInvitation[i]?.orgUnitId === finaOrgUnits?.id) {
              savedInvitationsContainFinaOrgUnit = true;
              break;
            }
            //Check to see if a FINA continent is a saved invitation
            for (let j = 0; j < finaOrgUnits.children?.length; j++) {
              if (selectedOmeMeetEntryObj?.omeEntryInvitation[i]?.orgUnitId === finaOrgUnits.children[j].id) {
                savedInvitationsContainFinaOrgUnit = true;
                break;
              }
              //Check to see if a FINA country is a saved invitation
              for (let k = 0; k < finaOrgUnits.children[j]?.children?.length; k++) {
                if (selectedOmeMeetEntryObj?.omeEntryInvitation[i]?.orgUnitId === finaOrgUnits.children[j]?.children[k]?.id) {
                  savedInvitationsContainFinaOrgUnit = true;
                  break;
                }
              }
            }
          }

          const hasFINAInvitation = savedInvitationsContainFinaOrgUnit;

          hasQualifyingWindow = omeMeetState.objData?.omeMeetQualification[0]?.hasTimeStandards === true ? ((selectedOmeMeetEntryObj?.omeMeetQualificationDate[0]?.timeStandardsQualifyingStartDate && formatDate(selectedOmeMeetEntryObj?.omeMeetQualificationDate[0]?.timeStandardsQualifyingStartDate) !== Constants.DEFAULT_MIN_DATE) ||
            (selectedOmeMeetEntryObj?.omeMeetQualificationDate[0]?.timeStandardsQualifyingEndDate && formatDate(selectedOmeMeetEntryObj?.omeMeetQualificationDate[0]?.timeStandardsQualifyingEndDate) !== formatDateTime(selectedOmeMeetEntryObj?.meetEntryEndDate)?.substring(0, 10)) ?
            true : false) : '';

          let costPerTeam = '';
          let costPerCredential = '';
          let costPerIndividualAthlete = '';
          let costPerIndividualInternationalAthlete = '';
          let costPerInternationalStaff = '';
          let costPerRelayOnlySwimmer = '';
          let costPerAthleteIndividualEventEntry = '';
          let costPerAthleteIndividualBonusEventEntry = '';
          let costPerRelayEventEntry = '';
          let currentCostPerTeam = '';
          let currentCostPerCredential = '';
          let currentCostPerIndividualAthlete = '';
          let currentCostPerIndividualInternationalAthlete = '';
          let currentCostPerInternationalStaff = '';
          let currentCostPerRelayOnlySwimmer = '';
          let currentCostPerAthleteIndividualEventEntry = '';
          let currentCostPerAthleteIndividualBonusEventEntry = '';
          let currentCostPerRelayEventEntry = '';
          for (let i = 0; i < selectedOmeMeetEntryObj?.omeMeetPrice?.length; i++) {
            if (selectedOmeMeetEntryObj.omeMeetPrice[i].omeFeeTypeId === TEAM_OME_FEE_TYPE_ID) {
              costPerTeam = selectedOmeMeetEntryObj.omeMeetPrice[i].amount;
              currentCostPerTeam = selectedOmeMeetEntryObj.omeMeetPrice[i].amount;
            } else if (selectedOmeMeetEntryObj.omeMeetPrice[i].omeFeeTypeId === CREDENTIAL_OME_FEE_TYPE_ID) {
              costPerCredential = selectedOmeMeetEntryObj.omeMeetPrice[i].amount;
              currentCostPerCredential = selectedOmeMeetEntryObj.omeMeetPrice[i].amount;
            } else if (selectedOmeMeetEntryObj.omeMeetPrice[i].omeFeeTypeId === INDIVIDUAL_ATHLETE_OME_FEE_TYPE_ID) {
              costPerIndividualAthlete = selectedOmeMeetEntryObj.omeMeetPrice[i].amount;
              currentCostPerIndividualAthlete = selectedOmeMeetEntryObj.omeMeetPrice[i].amount;
            } else if (selectedOmeMeetEntryObj.omeMeetPrice[i].omeFeeTypeId === INDIVIDUAL_INTL_ATHLETE_OME_FEE_TYPE_ID) {
              costPerIndividualInternationalAthlete = selectedOmeMeetEntryObj.omeMeetPrice[i].amount;
              currentCostPerIndividualInternationalAthlete = selectedOmeMeetEntryObj.omeMeetPrice[i].amount;
            } else if (selectedOmeMeetEntryObj.omeMeetPrice[i].omeFeeTypeId === INTL_STAFF_OME_FEE_TYPE_ID) {
              costPerInternationalStaff = selectedOmeMeetEntryObj.omeMeetPrice[i].amount;
              currentCostPerInternationalStaff = selectedOmeMeetEntryObj.omeMeetPrice[i].amount;
            } else if (selectedOmeMeetEntryObj.omeMeetPrice[i].omeFeeTypeId === RELAY_ONLY_SWIMMER_OME_FEE_TYPE_ID) {
              costPerRelayOnlySwimmer = selectedOmeMeetEntryObj.omeMeetPrice[i].amount;
              currentCostPerRelayOnlySwimmer = selectedOmeMeetEntryObj.omeMeetPrice[i].amount;
            } else if (selectedOmeMeetEntryObj.omeMeetPrice[i].omeFeeTypeId === ATHLETE_INDIVIDUAL_EVENT_ENTRY_OME_FEE_TYPE_ID) {
              costPerAthleteIndividualEventEntry = selectedOmeMeetEntryObj.omeMeetPrice[i].amount;
              currentCostPerAthleteIndividualEventEntry = selectedOmeMeetEntryObj.omeMeetPrice[i].amount;
            } else if (selectedOmeMeetEntryObj.omeMeetPrice[i].omeFeeTypeId === ATHLETE_INDIVIDUAL_BONUS_EVENT_ENTRY_OME_FEE_TYPE_ID) {
              costPerAthleteIndividualBonusEventEntry = selectedOmeMeetEntryObj.omeMeetPrice[i].amount;
              currentCostPerAthleteIndividualBonusEventEntry = selectedOmeMeetEntryObj.omeMeetPrice[i].amount;
            } else if (selectedOmeMeetEntryObj.omeMeetPrice[i].omeFeeTypeId === RELAY_EVENT_ENTRY_OME_FEE_TYPE_ID) {
              costPerRelayEventEntry = selectedOmeMeetEntryObj.omeMeetPrice[i].amount;
              currentCostPerRelayEventEntry = selectedOmeMeetEntryObj.omeMeetPrice[i].amount;
            }
          }

          setFormData({
            ...formState,
            meetStartDate: omeMeetState.selectedMeet?.startDate ? formatDate(omeMeetState.selectedMeet?.startDate) : Constants.BLANK_DATE_STRING,
            publishDate: omeMeetState.objData?.publishDate ? formatDateTime(omeMeetState.objData?.publishDate) : Constants.BLANK_DATETIME_STRING,
            omeMeetName: selectedOmeMeetEntryObj?.omeMeetName || '',
            isMainEntry: selectedOmeMeetEntryObj?.isMainEntry ?? false,
            meetEntryStartDate: selectedOmeMeetEntryObj?.meetEntryStartDate ? formatDateTime(selectedOmeMeetEntryObj?.meetEntryStartDate) : Constants.BLANK_DATETIME_STRING,
            meetEntryEndDate: selectedOmeMeetEntryObj?.meetEntryEndDate ? formatDateTime(selectedOmeMeetEntryObj?.meetEntryEndDate) : Constants.BLANK_DATETIME_STRING,
            meetEntryChangeDeadline: selectedOmeMeetEntryObj?.meetEntryChangeDeadline ? formatDateTime(selectedOmeMeetEntryObj?.meetEntryChangeDeadline) : Constants.BLANK_DATETIME_STRING,
            meetEntryStartDateTwelveHourClock: selectedOmeMeetEntryObj?.meetEntryStartDate ? formatDateTimeTwelveHourClock(selectedOmeMeetEntryObj?.meetEntryStartDate) : Constants.BLANK_DATETIME_STRING,
            meetEntryEndDateTwelveHourClock: selectedOmeMeetEntryObj?.meetEntryEndDate ? formatDateTimeTwelveHourClock(selectedOmeMeetEntryObj?.meetEntryEndDate) : Constants.BLANK_DATETIME_STRING,
            meetEntryChangeDeadlineTwelveHourClock: selectedOmeMeetEntryObj?.meetEntryChangeDeadline ? formatDateTimeTwelveHourClock(selectedOmeMeetEntryObj?.meetEntryChangeDeadline) : Constants.BLANK_DATETIME_STRING,
            relayEvents: omeMeetState?.objData?.omeMeetEligibility[0]?.relayEvents ?? '',
            allowRelayOnlySwimmer: omeMeetState?.objData.omeMeetEligibility[0]?.allowRelayOnlySwimmer ?? '',
            maxNumberOfAthletesForMeet: selectedOmeMeetEntryObj?.maxNumberOfAthletesForMeet !== null &&
              selectedOmeMeetEntryObj?.maxNumberOfAthletesForMeet !== undefined ?
              selectedOmeMeetEntryObj?.maxNumberOfAthletesForMeet : '',
            maxNumberOfAthletesCountsTowardsMainOmeMeet: selectedOmeMeetEntryObj?.maxNumberOfAthletesCountsTowardsMainOmeMeet ?? '',
            includeRelayOnlySwimmersInMaxNumber: selectedOmeMeetEntryObj?.includeRelayOnlySwimmersInMaxNumber ?? '',
            nationalTeamOnly: selectedOmeMeetEntryObj?.nationalTeamOnly ?? '',
            orgUnit: orgUnitIdArrayValues?.length > 0 ? HierarchicalDataUtils.GetNameIdPairs(orgUnitState.treeData, orgUnitIdArrayValues) || [] : [],
            hasTimeStandards: omeMeetState.objData?.omeMeetQualification[0]?.hasTimeStandards ?? '',
            hasQualifyingWindow: hasQualifyingWindow,
            timeStandardsQualifyingStartDate: selectedOmeMeetEntryObj?.omeMeetQualificationDate[0]?.timeStandardsQualifyingStartDate ?
              formatDate(selectedOmeMeetEntryObj?.omeMeetQualificationDate[0]?.timeStandardsQualifyingStartDate) : Constants.BLANK_DATE_STRING,
            timeStandardsQualifyingEndDate: selectedOmeMeetEntryObj?.omeMeetQualificationDate[0]?.timeStandardsQualifyingEndDate ?
              formatDate(selectedOmeMeetEntryObj?.omeMeetQualificationDate[0]?.timeStandardsQualifyingEndDate) : Constants.BLANK_DATE_STRING,
            costPerTeam,
            costPerCredential,
            costPerIndividualAthlete,
            costPerIndividualInternationalAthlete,
            costPerInternationalStaff,
            costPerRelayOnlySwimmer,
            costPerAthleteIndividualEventEntry,
            costPerAthleteIndividualBonusEventEntry,
            costPerRelayEventEntry,
            currentCostPerTeam,
            currentCostPerCredential,
            currentCostPerIndividualAthlete,
            currentCostPerIndividualInternationalAthlete,
            currentCostPerInternationalStaff,
            currentCostPerRelayOnlySwimmer,
            currentCostPerAthleteIndividualEventEntry,
            currentCostPerAthleteIndividualBonusEventEntry,
            currentCostPerRelayEventEntry,
            showCostPerIndividualInternationalAthlete: hasFINAInvitation,
            showCostPerInternationalStaff: hasFINAInvitation,
            showCostPerAthleteIndividualBonusEventEntry: hasBonusEvents,
            showCostPerRelayOnlySwimmer: hasRelayEvents === true && allowRelayOnlySwimmer === true ? true : false,
            showCostPerRelayEventEntry: hasRelayEvents
          });


          if (omeMeetState.objData?.omeMeetQualification[0]?.hasTimeStandards === true &&
            hasQualifyingWindow === false && selectedOmeMeetEntryObj?.omeMeetQualificationDate?.length === 0) {
            setIsDirty(true);
          }
        }
        else {
          //Add
          setFormData({
            ...formState,
            meetStartDate: omeMeetState.selectedMeet?.startDate ? formatDate(omeMeetState.selectedMeet?.startDate) : Constants.BLANK_DATE_STRING,
            publishDate: omeMeetState.objData?.publishDate ? formatDateTime(omeMeetState.objData?.publishDate) : Constants.BLANK_DATETIME_STRING,
            relayEvents: omeMeetState?.objData?.omeMeetEligibility[0]?.relayEvents ?? '',
            allowRelayOnlySwimmer: omeMeetState?.objData.omeMeetEligibility[0]?.allowRelayOnlySwimmer ?? '',
            hasTimeStandards: omeMeetState.objData?.omeMeetQualification[0]?.hasTimeStandards ?? '',
            showCostPerAthleteIndividualBonusEventEntry: hasBonusEvents,
            showCostPerRelayOnlySwimmer: hasRelayEvents === true && allowRelayOnlySwimmer === true ? true : false,
            showCostPerRelayEventEntry: hasRelayEvents
          });
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgUnitState.isArrayLoaded, omeMeetState.objData, selectedOmeMeetEntryState.initialLoadComplete, omeMeetEntryState, omeMeetEntryInvitationsState, dateTimeState]);

  useEffect(() => {
    if (Object.keys(omeMeetState.objData).length > 0) {
      const isSaved = formStateHasBeenSaved(formState);
      setLeftNavModalState({ ...leftNavModalState, formStateSaved: isSaved });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formState, omeMeetState.objData, omeMeetEntryState.objData]);

  useEffect(() => {
    if (omeMeetEntryState.isObjLoading === false && omeMeetEntryState.isSaving === false &&
      omeMeetEntryInvitationsState.isSaving === false && isSubmitting === false &&
      state.tryPostOmeMeetEntryInvitations === true) {
      //Edit
      if (selectedOmeMeetEntryState.selectedOmeMeetEntryId >= 0) {
        postOmeMeetEntryInvitations(selectedOmeMeetEntryState.selectedOmeMeetEntryId, state.updatedOmeEntryInvitationArray);
        setState({ ...state, prePutOmeMeetEntryStateObjData: {}, updatedOmeEntryInvitationArray: [], tryPostOmeMeetEntryInvitations: false, tryGetOmeMeetEntry: true });
      }
      //Add
      else {
        //Find the omeMeetEntryId of the new omeMeetEntry
        let selectedOmeMeetEntryId = Constants.DEFAULT_ID;
        for (let i = 0; i < omeMeetEntryState.objData?.omeMeetEntry?.length; i++) {
          let existingObj = state.prePutOmeMeetEntryStateObjData?.omeMeetEntry?.find(x => x.omeMeetEntryId === omeMeetEntryState.objData.omeMeetEntry[i].omeMeetEntryId)
          if (!existingObj) {
            selectedOmeMeetEntryId = omeMeetEntryState.objData.omeMeetEntry[i].omeMeetEntryId;
          }
        }
        if (selectedOmeMeetEntryId >= 0) {
          postOmeMeetEntryInvitations(selectedOmeMeetEntryId, state.updatedOmeEntryInvitationArray);
        }
        setSelectedOmeMeetEntryState({ ...selectedOmeMeetEntryState, selectedOmeMeetEntryId: selectedOmeMeetEntryId });
        setState({ ...state, prePutOmeMeetEntryStateObjData: {}, updatedOmeEntryInvitationArray: [], tryPostOmeMeetEntryInvitations: false, tryGetOmeMeetEntry: true });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSubmitting, omeMeetEntryState.isSaving, omeMeetEntryInvitationsState.isSaving, state]);

  useEffect(() => {
    if (omeMeetEntryState.isObjLoading === false && omeMeetEntryState.isSaving === false &&
      omeMeetEntryInvitationsState.isSaving === false && isSubmitting === false &&
      state.tryGetOmeMeetEntry === true) {
      getOmeMeetEntry(omeMeetEntryState.objData.omeMeetId);
      getOmeMeetDateValidation(omeMeetEntryState.objData.omeMeetId);
      if (state.doNotTryRedirect === true) {
        setState({ ...state, tryGetOmeMeetEntry: false, doNotTryRedirect: false });
      }
      else {
        setState({ ...state, tryGetOmeMeetEntry: false, tryRedirect: true });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSubmitting, omeMeetEntryState.isObjLoading, omeMeetEntryState.isSaving, omeMeetEntryInvitationsState.isSaving, state]);

  useEffect(() => {
    if (isReadyToRoute === true && omeMeetEntryState.isSaving === false && omeMeetEntryInvitationsState.isSaving === false
      && isSubmitting === false && state.tryRedirect === true) {
      getOmeMeetDateValidation(omeMeetEntryState.objData.omeMeetId);
      if (state.submitButton === HostRegistrationConstants.CONTINUE) {
        if (omeMeetState.route !== '') {
          navigate(NavLinks.MEET_HOST_OME_MEETS);
        }
        else {
          navigate(HostNavLinks.OME_MEET_OME_MEETS);
        }
      }
      else if (state.submitButton === HostRegistrationConstants.ADD) {
        if (omeMeetState.route !== '') {
          navigate(NavLinks.MEET_HOST_OME_MEETS_DETAIL_PRICING_ADDITIONAL_PURCHASES, { state: { detailFormState: formState, omeMeetEntryId: selectedOmeMeetEntryState.selectedOmeMeetEntryId } });
        }
        else {
          navigate(HostNavLinks.OME_MEET_OME_MEETS_DETAIL_PRICING_ADDITIONAL_PURCHASES, { state: { detailFormState: formState, omeMeetEntryId: selectedOmeMeetEntryState.selectedOmeMeetEntryId } });
        }
      }
      else if (state.submitButton === HostRegistrationConstants.EDIT) {
        if (omeMeetState.route !== '') {
          navigate(NavLinks.MEET_HOST_OME_MEETS_DETAIL_PRICING_ADDITIONAL_PURCHASES, { state: { detailFormState: formState, omeMeetPriceId: state.omeMeetPriceId, omeMeetEntryId: selectedOmeMeetEntryState.selectedOmeMeetEntryId } });
        }
        else {
          navigate(HostNavLinks.OME_MEET_OME_MEETS_DETAIL_PRICING_ADDITIONAL_PURCHASES, { state: { detailFormState: formState, omeMeetPriceId: state.omeMeetPriceId, omeMeetEntryId: selectedOmeMeetEntryState.selectedOmeMeetEntryId } });
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSubmitting, omeMeetEntryState.isSaving, omeMeetEntryInvitationsState.isSaving, state, isReadyToRoute]);

  function allowAllEdits() {
    //Keep most of the fields editable up until the meet end date
    if (omeMeetState.objData?.status === Constants.WORKFLOW_STATUS_CREATED) {
      const today = new Date();
      const meetEndDate = new Date(formatDate(omeMeetState.selectedMeet?.endDate));
      if (today > meetEndDate) {
        return false;
      }
    }

    return true;
  }

  function allowSomeEdits() {
    //Don't allow pricing changes if the meet has entries (cannot be deleted) or if it's after the meet entry start date
    if (omeMeetState.objData?.status === Constants.WORKFLOW_STATUS_CREATED) {
      const selectedOmeMeetEntryObj = omeMeetEntryState.objData?.omeMeetEntry?.find(x => x.omeMeetEntryId === selectedOmeMeetEntryState.selectedOmeMeetEntryId);
      if (selectedOmeMeetEntryObj) {
        const today = new Date();
        const meetEntryStartDate = new Date(formatDateTime(selectedOmeMeetEntryObj.meetEntryStartDate));
        if ((today > meetEntryStartDate) || selectedOmeMeetEntryObj.canBeDeleted === false) {
          return false;
        }
      }
    }

    return true;
  }

  return {
    state,
    selectedOmeMeetEntryState,
    omeMeetState,
    omeMeetEntryState,
    omeMeetEntryInvitationsState,
    organizationInvitations: state?.organizationInvitations,
    orgUnitState,
    dateTimeState,
    omeMeetDateValidationState,
    formState,
    errorState,
    handleSubmit,
    onNextButtonClicked,
    onBackButtonClicked,
    onFormValueChanged,
    onOrgUnitFormValueChanged,
    onAddAdditionalPurchaseItem,
    onEditAdditionalPurchaseItem,
    onDeleteAdditionalPurchaseItem,
    onDeleteAdditionalPurchaseItemClicked,
    modalState,
    onModalCanceled,
    allowAllEdits,
    allowSomeEdits,
    formStateHasBeenSaved,
    editOmeMeetEntryCopy
  };
};

export default useOmeMeetsDetail;